import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { clearAsset } from '../state';
import { useGetAssetByIdQuery } from '../../../services/AssetService/assetApi';
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { AssetEditHeader, AssetEditForm } from '../components/AssetForm';
import { AssetLoadingState, AssetErrorState } from '../components/shared';

/**
 * AssetEdit - Main page component for editing existing assets
 * Loads asset data from API using query parameters and provides editing interface
 */
const AssetEdit: React.FC = () => {
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get('id') || '';
  const dispatch = useAppDispatch();

  const {
    isLoading: isLoadingAsset,
    isError,
    error,
  } = useGetAssetByIdQuery(assetId, {
    skip: !assetId,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (assetId) {
      dispatch(clearAsset());
    }
  }, [dispatch, assetId]);

  if (isLoadingAsset) {
    return <AssetLoadingState message="Loading asset details..." />;
  }

  if (isError || !assetId) {
    const errorMessage = !assetId
      ? 'No asset ID provided. Please provide a valid asset ID in the URL.'
      : 'Failed to load asset. Please check the asset ID and try again.';
    const errorDetails = isError && error && 'message' in error
      ? (error as any).message
      : undefined;

    return <AssetErrorState message={errorMessage} details={errorDetails} />;
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <AssetEditHeader />
          <AssetEditForm />
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default AssetEdit;
